import { Link, useRouter, type ErrorComponentProps } from "@tanstack/react-router";
import { Compass, Home, RefreshCw } from "lucide-react";
import { LogoMark } from "@/components/brand/brand";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/misc";
import { ErrorState } from "@/components/ui/states";
import { useT } from "@/lib/i18n";

/** Shown while a lazy route chunk or its loader is on its way. */
export function RoutePending() {
  return (
    <div className="mx-auto w-full max-w-[1320px] px-4 pt-6 sm:px-6" aria-busy="true">
      <Skeleton className="mb-2 h-7 w-56" />
      <Skeleton className="mb-6 h-4 w-80 max-w-full" />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} className="h-28 rounded-[20px]" />
        ))}
      </div>
      <Skeleton className="mt-4 h-72 rounded-[20px]" />
    </div>
  );
}

/** A screen that breaks takes only itself down: the shell stays, and navigation resets it. */
export function RouteError({ error, reset }: ErrorComponentProps) {
  const { t } = useT();
  const router = useRouter();
  return (
    <div className="mx-auto max-w-lg px-4 py-16">
      <div className="rounded-3xl border border-line bg-surface p-2 shadow-xs">
        <ErrorState error={error} />
        <div className="flex flex-wrap justify-center gap-2 pb-6">
          <Button
            variant="primary"
            icon={<RefreshCw />}
            onClick={() => {
              reset();
              void router.invalidate();
            }}
          >
            {t("common.actions.retry")}
          </Button>
          <Button variant="ghost" icon={<Home />} asChild>
            <Link to="/">{t("common.errors.goHome")}</Link>
          </Button>
        </div>
        {import.meta.env.DEV && error instanceof Error && (
          <pre dir="ltr" className="mx-4 mb-4 max-h-40 overflow-auto rounded-xl bg-surface-2 p-3 text-[11px] text-ink-2">
            {error.stack ?? error.message}
          </pre>
        )}
      </div>
    </div>
  );
}

export function NotFound() {
  const { t } = useT();
  return (
    <div className="grid min-h-[60vh] place-items-center px-4 py-16 text-center">
      <div>
        <div className="relative mx-auto mb-6 w-fit">
          <div className="absolute inset-0 scale-[2] rounded-full opacity-60 blur-3xl [background:var(--brand-grad-soft)]" />
          <LogoMark size={64} className="relative" />
        </div>
        <p className="num text-[56px] font-semibold leading-none tracking-tight brand-text">404</p>
        <h1 className="mt-3 text-[20px] font-semibold text-ink">{t("common.errors.notFoundTitle")}</h1>
        <p className="mx-auto mt-2 max-w-sm text-[14px] text-ink-3">{t("common.errors.notFoundHint")}</p>
        <Button variant="primary" className="mt-6" icon={<Compass />} asChild>
          <Link to="/">{t("common.errors.goHome")}</Link>
        </Button>
      </div>
    </div>
  );
}
